import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import getLuxPlusValueSavedThisYear from 'luxPlus/selectors/benefits/getLuxPlusValueSavedThisYear'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LuxPlusManageAccountModalBenefitsList from './LuxPlusManageAccountModalBenefitsList'
import LuxPlusRetentionModalFooter from './LuxPlusRetentionModalFooter'

// ModalContent only exposes a top flush; flush the top and keep a little bottom breathing room.
// The modal scrolls natively via ModalBody, so the header/footer scroll fade applies automatically.
const FlushVerticalModalContent = styled(ModalContent)`
  padding-top: 0;
  padding-bottom: ${rem(16)};
`

interface Props {
  item: App.MemberSubscriptionItem
  onCancelRenewal: () => void
}

const messages = defineMessages({
  title: {
    id: 'XqKxTcN3iqwlc_nDKp3U',
    defaultMessage: 'Keep enjoying your member benefits',
    description: 'Title of the LuxPlus+ renewal cancellation retention modal',
  },
  savedThisYear: {
    id: 'Wv_Q5DoEYizSD_FmDOku',
    defaultMessage: "This year alone, you've saved {savings} with {programName}.",
    description: 'Intro line showing how much the member has saved this year',
  },
  benefitsActiveUntil: {
    id: 'v3FChJRaBgfeBTg2vCf7',
    defaultMessage: "Your {programName} benefits will remain active until <b>{date}</b>. After this date, you'll no longer have access to member-only pricing and year-round benefits.",
    description: 'Intro line explaining when member benefits end after cancelling renewal',
  },
  benefitsHeading: {
    id: 'GwIxpYEok_sR60xDt6Uw',
    defaultMessage: 'Your {programName} benefits',
    description: 'Heading above the member benefits list in the renewal cancellation modal',
  },
})

/**
 * Renewal-cancellation retention screen ("Keep enjoying your member benefits") shown to
 * loyalty-enabled members. The non-loyalty-member design lives in
 * {@link LuxPlusManageAccountModalRenewalCancellationConfirmation}.
 */
function LuxPlusManageAccountModalKeepBenefits({
  item,
  onCancelRenewal,
}: Props) {
  const intl = useIntl()

  const savedValue = useAppSelector(getLuxPlusValueSavedThisYear)
  // Only lead with the savings figure once it beats the cost of the membership (current period price),
  // mirroring the previous savings-banner threshold.
  const showSavings = item.currentSubscriptionPrice !== undefined && savedValue > item.currentSubscriptionPrice

  return <>
    <ModalHeader title={intl.formatMessage(messages.title)} />
    <ModalBody>
      <FlushVerticalModalContent>
        <Group direction="vertical" gap={20}>
          <Group direction="vertical" gap={12}>
            {showSavings && <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
              <FormattedMessage
                {...messages.savedThisYear}
                values={{
                  savings: <FormatCurrency value={savedValue} />,
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                }}
              />
            </BodyText>}
            <BodyText variant="medium" colour="light-foreground-alt">
              <FormattedMessage
                {...messages.benefitsActiveUntil}
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  date: <FormatDateTime value={item.expiryDate} format="dmy-casual" />,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>
          </Group>
          <Group direction="vertical" gap={16}>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage(messages.benefitsHeading, { programName: LUXURY_PLUS.PROGRAM_NAME })}
            </BodyText>
            <LuxPlusManageAccountModalBenefitsList />
          </Group>
        </Group>
      </FlushVerticalModalContent>
    </ModalBody>
    <LuxPlusRetentionModalFooter onContinueToCancel={onCancelRenewal} />
  </>
}

export default LuxPlusManageAccountModalKeepBenefits
